import React from 'react';

export default function HeroSection({ isLoaded }) {
    return (
        <main className="relative w-full h-[640px] md:h-[776px] overflow-hidden">
            <div className="container mx-auto px-4 lg:px-6 pt-10 md:pt-14 pb-0 flex flex-col items-center h-full">

                {/* Text Content */}
                <div className={`text-center max-w-4xl mx-auto space-y-6 relative z-10 transition-all duration-1000 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

                    {/* Mobile Only Content */}
                    <div className="block md:hidden text-left space-y-6 px-4">
                        <h1 className="text-4xl font-bold text-white leading-[1.1] tracking-tight">
                            Build, Launch &<br />Grow Continuously.
                        </h1>
                        <p className="text-white/90 text-lg leading-relaxed max-w-xs font-light">
                            We simplify product decisions, execution, and growth-so your team moves faster with clarity.
                        </p>
                        <a href="#book-call" className="flex items-center gap-2 px-8 py-3 rounded-full border border-white text-white text-lg font-medium hover:bg-white/10 transition-colors w-fit group">
                            Book a Call
                            <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                        </a>
                    </div>

                    {/* Desktop Only Content */}
                    <div className="hidden md:block space-y-6">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold text-white leading-tight tracking-tight drop-shadow-sm">
                            Build, Launch & Scale Digital Products
                        </h1>

                        <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white font-medium">
                            <span className="opacity-90 text-[clamp(1.5rem,3vw,2.5rem)]">▼ with</span>
                            <div className="bg-white/20 backdrop-blur-md px-6 py-2 rounded-2xl border border-white/30 text-white font-bold tracking-wide shadow-lg text-[clamp(1.5rem,3vw,2.5rem)]">
                                Zenruva
                            </div>
                            <span className="opacity-90 text-[clamp(1.5rem,3vw,2.5rem)]">▼</span>
                        </div>

                        <p className="text-lg md:text-xl text-blue-50/90 max-w-3xl mx-auto leading-relaxed pt-1 font-light">
                            We help ambitious startups and growing companies turn ideas into digital products people love to use. Designed for users. Built for performance. Proven to scale.
                        </p>
                    </div>
                </div>

                {/* Visual Section */}
                <div className={`relative mt-8 md:mt-12 w-full max-w-[1400px] flex-grow flex justify-center transition-all duration-1000 delay-300 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>


                    {/* Mobile Static Image */}
                    <img src="/images/mobile-hero.png" alt="Zenruva Mobile Hero" className="w-[120%] max-w-none h-auto object-contain drop-shadow-2xl md:hidden" />
                </div>

                {/* Desktop Interactive Elements */}
                <div className="hidden md:block w-full h-full relative">

                    {/* Strategy Pill - Floating Top Right relative to phone */}
                    <div className="absolute top-[5%] right-[5%] md:top-[-5%] md:right-[8%] z-20 animate-float-slow">
                        <div className="bg-[#0b5cd5] text-white px-6 py-2 rounded-full border-4 border-white shadow-[0_8px_30px_rgb(0,0,0,0.2)] text-base font-medium backdrop-blur-md">
                            Strategy
                        </div>
                    </div>

                    {/* UI/UX Pill - Floating Top Left relative to phone */}
                    <div className="absolute top-[10%] left-[30%] md:top-[15%] md:left-[15%] z-20 animate-float-reverse">
                        <div className="bg-[#0b5cd5] text-white pl-5 pr-3 py-2 rounded-full border-4 border-white shadow-[0_8px_30px_rgb(0,0,0,0.2)] flex items-center gap-2 text-base font-medium backdrop-blur-md">
                            UI/UX
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                        </div>
                    </div>


                    {/* UI/UX Design Card - Left Side */}
                    <div className="absolute top-[30%] left-[2%] lg:left-[8%] z-30 hidden md:block animate-float-slow">
                        <div className="bg-white/95 backdrop-blur-sm rounded-[1.5rem] p-4 shadow-2xl w-[240px] lg:w-[280px] relative border border-white/60 scale-90 lg:scale-100 origin-left">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-[#2bbaea] rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                </div>
                                <span className="font-bold text-gray-900 text-base">UI/UX Design</span>
                            </div>
                            <div className="space-y-2">
                                <div className="bg-gray-50 p-2.5 rounded-xl flex items-center gap-3 border border-gray-100">
                                    <div className="w-7 h-7 bg-gray-200/50 rounded-lg flex items-center justify-center text-gray-500">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2zM16 13a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1v-2zM4 21a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2z" /></svg>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-gray-800">Wireframes</div>
                                        <div className="text-[9px] text-gray-500 font-medium tracking-wide">Prototypes</div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 p-2.5 rounded-xl flex items-center gap-3 border border-gray-100">
                                    <div className="w-7 h-7 bg-gray-200/50 rounded-lg flex items-center justify-center text-gray-500">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-gray-800">Design System</div>
                                        <div className="text-[9px] text-gray-500 font-medium tracking-wide">Components</div>
                                    </div>
                                </div>
                            </div>

                            {/* Development Badge - Attached to bottom right */}
                            <div className="absolute -right-2 -bottom-4 z-40 transform rotate-[0deg]">
                                <div className="bg-[#0b5cd5] text-white px-4 py-2 rounded-full border-2 border-white shadow-xl text-xs font-bold">
                                    Development
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Project Progress Card - Right Top */}
                    <div className="absolute top-[25%] right-[2%] md:top-[15%] md:right-[5%] lg:right-[7%] z-20 hidden md:block animate-float-reverse">
                        <div className="bg-white/95 backdrop-blur-sm rounded-[1.5rem] p-4 shadow-2xl w-[250px] border border-white/60 scale-120 lg:scale-110 origin-right">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-9 h-9 bg-[#2bbaea] rounded-full flex items-center justify-center text-white p-2 shadow-sm">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                </div>
                                <span className="font-bold text-gray-900 text-base">Project Progress</span>
                            </div>

                            <div className="flex justify-between text-[9px] text-gray-500 mb-2 px-1 font-medium tracking-wide">
                                <span>Discover</span>
                                <span>Design</span>
                                <span>Dev</span>
                                <span className="bg-[#2bbaea] text-white px-2 py-0.5 rounded text-[9px] shadow-sm">Launch</span>
                            </div>
                            <div className="relative h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="absolute top-0 left-0 h-full w-[85%] bg-[#0054DB] rounded-full" />
                                <div className="absolute top-1/2 left-[83%] -translate-y-1/2 w-4 h-4 bg-[#2bbaea] border-[2px] border-white rounded-full shadow-sm z-10" />
                            </div>
                        </div>
                    </div>

                    {/* Development Card - Right Bottom */}
                    <div className="absolute bottom-[20%] right-[0%] md:bottom-[5%] md:right-[8%] lg:right-[16%] z-30 hidden md:block animate-float-slow" style={{ animationDelay: '1.5s' }}>
                        <div className="bg-white/95 backdrop-blur-sm rounded-[1.5rem] p-4 shadow-2xl w-[240px] border border-white/60 scale-90 lg:scale-100 origin-right">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-9 h-9 bg-[#2bbaea] rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
                                </div>
                                <span className="font-bold text-gray-900 text-base">Development</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <span className="text-[9px] px-2.5 py-1 bg-gray-50 rounded-xl text-gray-700 border border-gray-100 font-bold">Web Apps</span>
                                <span className="text-[9px] px-2.5 py-1 bg-gray-50 rounded-xl text-gray-700 border border-gray-100 font-bold">Mobile Apps</span>
                                <span className="text-[9px] px-2.5 py-1 bg-[#2bbaea] rounded-xl text-white font-bold shadow-md shadow-blue-200">SaaS</span>
                            </div>
                        </div>
                    </div>


                    {/* Phone Mockup - Center Bottom Clipped */}
                    <div className="absolute bottom-[0%] sm:bottom-[0%] left-1/2 transform -translate-x-1/2 z-10 w-[240px] sm:w-[280px] md:w-[320px] lg:w-[360px]">
                        <img
                            src="/images/hero-phone-updated.png"
                            alt="Zenruva App"
                            className="w-full h-auto object-contain drop-shadow-2xl"
                        />
                    </div>
                </div>

            </div>
        </main>
    );
}